import React, { memo, useMemo, useRef, useState, useLayoutEffect } from "react";
import { faExclamationCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FixedSizeGrid as Grid, areEqual } from "react-window";

const CanvasImagePreview = memo(function CanvasImagePreview({ source, alt }) {
  const canvasRef = useRef(null);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !source?.naturalWidth || !source?.naturalHeight) return;

    const ctx = canvas.getContext("2d");
    const displayWidth = canvas.clientWidth || 90;
    const displayHeight = canvas.clientHeight || 60;
    const pixelRatio = window.devicePixelRatio || 1;

    canvas.width = Math.max(1, Math.round(displayWidth * pixelRatio));
    canvas.height = Math.max(1, Math.round(displayHeight * pixelRatio));

    const sourceRatio = source.naturalWidth / source.naturalHeight;
    const targetRatio = displayWidth / displayHeight;
    let sourceWidth = source.naturalWidth;
    let sourceHeight = source.naturalHeight;
    let sourceX = 0;
    let sourceY = 0;

    if (sourceRatio > targetRatio) {
      sourceWidth = source.naturalHeight * targetRatio;
      sourceX = (source.naturalWidth - sourceWidth) / 2;
    } else {
      sourceHeight = source.naturalWidth / targetRatio;
      sourceY = (source.naturalHeight - sourceHeight) / 2;
    }

    ctx.drawImage(
      source,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );
  }, [source]);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={alt}
      style={{ width: "100%", height: 60, borderRadius: 6, objectFit: "cover" }}
    />
  );
});

const Cell = memo(({ columnIndex, rowIndex, style, data }) => {
  const { items, columnCount, onRemove, flaggedSet } = data;
  const index = rowIndex * columnCount + columnIndex;

  if (index >= items.length) return null;

  const item = items[index];
  const isFlagged = flaggedSet.has(item.name);

  return (
    <div style={{ ...style, padding: 6, boxSizing: "border-box" }}>
      <div style={{ position: "relative" }}>
        {item.type === "video" ? (
          <video
            src={item.url}
            preload="metadata"
            muted
            playsInline
            style={{ width: "100%", height: 60, borderRadius: 6, objectFit: "cover" }}
          />
        ) : item.previewElement ? (
          <CanvasImagePreview source={item.previewElement} alt={item.name} />
        ) : (
          <img
            src={item.url}
            alt={item.name}
            style={{ width: "100%", height: 60, borderRadius: 6, objectFit: "cover" }}
          />
        )}

        {isFlagged && (
          <FontAwesomeIcon
            icon={faExclamationCircle}
            style={{
              position: "absolute",
              top: -4,
              left: -4,
              color: "red",
              background: "white",
              borderRadius: "50%",
            }}
          />
        )}

        <FontAwesomeIcon
          icon={faTimesCircle}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(index);
          }}
          style={{
            position: "absolute",
            top: -4,
            right: -4,
            cursor: "pointer",
            background: "white",
            borderRadius: "50%",
          }}
        />
      </div>
    </div>
  );
}, areEqual);

const VirtualImageGrid = memo(function VirtualImageGrid({
  items,
  height = 300,
  minColumnWidth = 90,
  rowHeight = 90,
  onRemove,
  flaggedImages = [],
}) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const element = containerRef.current;

    const updateWidth = () => {
      setContainerWidth(element.offsetWidth);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const flaggedSet = useMemo(() => new Set(flaggedImages), [flaggedImages]);

  const columnCount = Math.max(1, Math.floor(containerWidth / minColumnWidth));
  const columnWidth = columnCount > 0 ? Math.floor(containerWidth / columnCount) : minColumnWidth;
  const rowCount = Math.ceil(items.length / columnCount);

  const itemData = useMemo(
    () => ({ items, columnCount, onRemove, flaggedSet }),
    [items, columnCount, onRemove, flaggedSet]
  );

  const itemKey = ({ columnIndex, rowIndex, data }) => {
    const idx = rowIndex * data.columnCount + columnIndex;
    const it = data.items[idx];
    return it?.name || `${rowIndex}:${columnIndex}`;
  };

  return (
    <div ref={containerRef} style={{ width: "100%", minWidth: 0, overflowX: "hidden" }}>
      {containerWidth > 0 && (
        <Grid
          columnCount={columnCount}
          columnWidth={columnWidth}
          height={height}
          rowCount={rowCount}
          rowHeight={rowHeight}
          width={containerWidth}
          style={{ overflowX: "hidden", overflowY: "auto" }}
          itemData={itemData}
          itemKey={itemKey}
        >
          {Cell}
        </Grid>
      )}
    </div>
  );
});

export default VirtualImageGrid;
